import { apiClient, type Paginated } from '@/core/api';

import type {
  KnowledgeBase,
  KnowledgeBaseInput,
  KnowledgeChunk,
  KnowledgeDocument,
  KnowledgeSearchInput,
} from './types';

// The knowledge_base app is mounted at /api/knowledge-base/; the routers add the
// resource segment. Detail routes accept public_id OR int id.
const BASES = '/knowledge-base/bases/';
const DOCUMENTS = '/knowledge-base/documents/';
const SEARCH = '/knowledge-base/search/';
const TAGS = '/knowledge-base/tags/';
const SOURCE_STATUS = '/knowledge-base/source-status/';
const EMBEDDING_CONFIG = '/knowledge-base/embedding-config/';
const SYNC_SOURCE = '/knowledge-base/sync-source/';
const SYNC_PROJECT = '/knowledge-base/sync-project/';
const SYNC_COLLECTION = '/knowledge-base/sync-collection/';

// ---- Tags (distinct chunk tags in this org — for tag pickers) ----
export const tagsApi = {
  list: async (): Promise<string[]> => (await apiClient.get<{ tags: string[] }>(TAGS)).data.tags,
};

// ---- Knowledge bases ----
const basesCrud = {
  list: async (params: Record<string, unknown> = {}): Promise<Paginated<KnowledgeBase>> =>
    (await apiClient.get<Paginated<KnowledgeBase>>(BASES, { params })).data,
  get: async (id: string | number): Promise<KnowledgeBase> =>
    (await apiClient.get<KnowledgeBase>(`${BASES}${id}/`)).data,
  create: async (input: KnowledgeBaseInput): Promise<KnowledgeBase> =>
    (await apiClient.post<KnowledgeBase>(BASES, input)).data,
  update: async (id: string | number, input: Partial<KnowledgeBaseInput>): Promise<KnowledgeBase> =>
    (await apiClient.patch<KnowledgeBase>(`${BASES}${id}/`, input)).data,
  remove: async (id: string | number): Promise<void> => {
    await apiClient.delete(`${BASES}${id}/`);
  },
};

export const basesApi = {
  ...basesCrud,
  /** Re-index every document in the base. Returns 202. */
  sync: async (id: string | number): Promise<void> => {
    await apiClient.post(`${BASES}${id}/sync/`);
  },
};

// ---- Documents ----
export const documentsApi = {
  list: async (knowledgeBaseId: number): Promise<Paginated<KnowledgeDocument>> =>
    (await apiClient.get<Paginated<KnowledgeDocument>>(DOCUMENTS, {
      params: { knowledge_base: knowledgeBaseId },
    })).data,
  /** Upload a file as a new document (multipart). Optional retrieval tags. */
  upload: async (input: {
    knowledgeBaseId: number;
    title: string;
    file: File;
    tags?: string[];
  }): Promise<KnowledgeDocument> => {
    const form = new FormData();
    form.append('knowledge_base', String(input.knowledgeBaseId));
    form.append('title', input.title);
    form.append('source_kind', 'upload');
    form.append('file', input.file);
    form.append('mime', input.file.type || 'application/octet-stream');
    for (const tag of input.tags ?? []) form.append('tags', tag);
    return (await apiClient.post<KnowledgeDocument>(DOCUMENTS, form)).data;
  },
  /** Patch a document's metadata (e.g. tags) — JSON, no re-upload. */
  update: async (
    id: string | number,
    input: { title?: string; tags?: string[] },
  ): Promise<KnowledgeDocument> =>
    (await apiClient.patch<KnowledgeDocument>(`${DOCUMENTS}${id}/`, input)).data,
  resync: async (id: string | number): Promise<KnowledgeDocument> =>
    (await apiClient.post<KnowledgeDocument>(`${DOCUMENTS}${id}/resync/`)).data,
  remove: async (id: string | number): Promise<void> => {
    await apiClient.delete(`${DOCUMENTS}${id}/`);
  },
};

// ---- Semantic search ----
/** POST /knowledge-base/search/ → matching chunks. 503 when the embedder is unconfigured. */
export async function searchKnowledge(input: KnowledgeSearchInput): Promise<KnowledgeChunk[]> {
  const { data } = await apiClient.post<KnowledgeChunk[]>(SEARCH, input);
  return data;
}

// ---- Sync sources into a base (used by the wiki / project sync buttons) ----
/** Sync one entity ("app_label.model" + id) into RAG. Returns 202. */
export async function syncSource(input: {
  entity: string;
  entity_id: number;
  knowledge_base_id?: number;
}): Promise<void> {
  await apiClient.post(SYNC_SOURCE, input);
}

/** Sync an entire project (all its syncable entities) into RAG. Returns 202. */
export async function syncProject(projectId: number): Promise<void> {
  await apiClient.post(SYNC_PROJECT, { project_id: projectId });
}

/** Sync a whole wiki collection (all its pages) into its KB. Returns 202. */
export async function syncCollection(collectionId: number): Promise<void> {
  await apiClient.post(SYNC_COLLECTION, { collection_id: collectionId });
}

// ---- Embedding config (per-org OpenAI key; Founder/Admin; never returns raw) ----
export interface EmbeddingConfig {
  configured: boolean;
  masked: string | null;
  model: string;
}

export const embeddingConfigApi = {
  get: async (): Promise<EmbeddingConfig> =>
    (await apiClient.get<EmbeddingConfig>(EMBEDDING_CONFIG)).data,
  set: async (apiKey: string): Promise<EmbeddingConfig> =>
    (await apiClient.put<EmbeddingConfig>(EMBEDDING_CONFIG, { api_key: apiKey })).data,
  clear: async (): Promise<EmbeddingConfig> =>
    (await apiClient.delete<EmbeddingConfig>(EMBEDDING_CONFIG)).data,
};

// ---- Source sync status (has this entity been synced into a KB yet?) ----
export interface SourceStatus {
  entity_id: number;
  synced: boolean;
  document_id: string | null;
  knowledge_base_id: number | null;
  status: KnowledgeDocument['status'] | null;
  last_synced_at: string | null;
}

/**
 * Look up whether the given entities ("app_label.model" + ids) have been synced
 * into a KB. Returns one entry per requested id (order preserved). Used to show
 * the "already synced" badge + flip the menu to "Re-sync".
 */
export async function fetchSourceStatus(
  entity: string,
  ids: number[],
): Promise<SourceStatus[]> {
  if (ids.length === 0) return [];
  const { data } = await apiClient.get<{ results: SourceStatus[] }>(SOURCE_STATUS, {
    params: { entity, ids: ids.join(',') },
  });
  return data.results;
}
